import {
    CoreUser,
    useListUsersForValidatorAccounts,
} from 'src/features/validatorAccounts/api/useListUsersForValidatorAccounts';
import DataTableColumnHeader from 'src/registry/components/table/DataTableColumnHeader';
import { userMessages } from 'src/features/users/utils/messages';
import { CellBoolean } from 'src/registry/components/table/cells/CellBoolean';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';
import { createColumnHelper } from '@tanstack/react-table';
import useFilters from 'src/registry/hooks/useFilters';
import useSorting from 'src/registry/hooks/useSorting';
import useSearch from 'src/registry/hooks/useSearch';
import usePagination from 'src/registry/hooks/usePagination';
import { DataTable } from 'src/registry/components/table/DataTable';
import { DataTableControls, DataTablePagination } from 'src/registry/components/table';
import React from 'react';
import { useParking } from 'src/providers/ParkingProvider';
import { booleanBadgeOptions } from 'src/utils/constants';

const sortingOptions = {
    initialSortingState: [{ id: 'name', desc: false }],
};
const searchOptions = { globalFilterKey: 'name' };

const ValidatorAccountUsers = () => {
    const { formatMessage } = useIntl();
    const { accountId } = useParams();
    const { parkingId } = useParking();

    const columnHelper = createColumnHelper<CoreUser>();

    const { queryFiltersOptions, ...filters } = useFilters();
    const { querySearchOptions, ...search } = useSearch(searchOptions);
    const { querySortingOptions, ...sorting } = useSorting(sortingOptions);
    const { queryPaginationOptions, ...pagination } = usePagination();

    const payload = React.useMemo(
        () => ({
            parkingId,
            ...(accountId && { validator_account: accountId }),
            ...queryFiltersOptions,
            ...querySearchOptions,
            ...querySortingOptions,
            ...queryPaginationOptions,
        }),
        [queryFiltersOptions, querySearchOptions, querySortingOptions, accountId, queryPaginationOptions, parkingId],
    );

    const { data, isLoading } = useListUsersForValidatorAccounts(payload);

    const columns = [
        columnHelper.accessor('name', {
            header: DataTableColumnHeader,
            enableSorting: true,
            meta: {
                title: formatMessage(userMessages.name),
            },
        }),
        columnHelper.accessor('username', {
            header: DataTableColumnHeader,
            enableSorting: true,
            meta: {
                title: formatMessage(userMessages.username),
            },
        }),
        columnHelper.accessor('email', {
            header: DataTableColumnHeader,
            enableSorting: true,
            meta: {
                title: formatMessage(userMessages.email),
            },
        }),
        columnHelper.accessor('is_active', {
            header: DataTableColumnHeader,
            enableSorting: true,
            enableColumnFilter: true,
            cell: CellBoolean,
            meta: {
                title: formatMessage(userMessages.active),
                badgeOptions: booleanBadgeOptions,
            },
        }),
    ];

    return (
        <DataTable<CoreUser, any>
            isLoading={isLoading}
            columns={columns}
            data={data}
            search={search}
            filters={filters}
            sorting={sorting}
            pagination={pagination}
            semantics={{
                title: formatMessage(userMessages.users),
                entryName: formatMessage(userMessages.user).toLowerCase(),
                entryNames: formatMessage(userMessages.users).toLowerCase(),
                placeholder: formatMessage(userMessages.user),
            }}
            components={{
                controls: DataTableControls,
                pagination: DataTablePagination,
            }}
        />
    );
};

export default ValidatorAccountUsers;
